import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import Axios from "axios";
import { API, API_EPICENTER, API_STUDENT_PORTAL, API_CY_SSO } from "../Services/api-service";

/**
 * Custom hook for making API requests with built-in state management.
 *
 * @param {string} url - The API endpoint URL
 * @param {Object} [options] - Configuration options
 * @param {string} [options.method='get'] - HTTP method (get, post, put, delete)
 * @param {Object} [options.params={}] - Query parameters for the request
 * @param {Object} [options.data={}] - Request body data
 * @param {Object} [options.config={}] - Additional axios config
 * @param {Function} [options.onSuccess] - Callback on successful response
 * @param {Function} [options.onError] - Callback on error
 * @param {boolean} [options.enabled=true] - Whether the request should auto-fire
 * @param {boolean} [options.manual=false] - If true, request only fires via refetch/mutate
 * @param {'default'|'epicenter'|'studentPortal'} [options.apiSource='default'] - Which API instance to use ('default' for API, 'epicenter' for API_EPICENTER, 'studentPortal' for API_STUDENT_PORTAL)
 * @returns {{ apiData: any, loading: boolean, error: any, refetch: Function, mutate: Function }}
 *
 * A failed request is reported through `error` and `onError`. Only `mutate` also rejects, because
 * its caller awaits it; the effect that fires the request and `refetch` have no caller to catch a
 * rejection, so they never reject.
 */
function useFetch(url, options = {}) {
    const {
        method = "get",
        params = {},
        data = {},
        config = {},
        onSuccess,
        onError,
        enabled = true,
        manual = false,
        apiSource = "default",
    } = options;

    const apiSources = {
        default: API,
        epicenter: API_EPICENTER,
        studentPortal: API_STUDENT_PORTAL,
        cySSO: API_CY_SSO,
    };

    const apiInstance = apiSources[apiSource] || API;

    const [apiData, setData] = useState(null);
    // Auto-firing requests are already pending on the first render — the effect
    // below runs after it. Starting at false made every consumer paint its empty
    // state before the call even started. Manual/disabled hooks start idle.
    const [loading, setLoading] = useState(enabled && !manual);
    const [error, setError] = useState(null);

    const onSuccessRef = useRef(onSuccess);
    const onErrorRef = useRef(onError);

    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;

    const paramsKey = JSON.stringify(params);
    const dataKey = JSON.stringify(data);
    const configKey = JSON.stringify(config);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoizedParams = useMemo(() => params, [paramsKey]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoizedData = useMemo(() => data, [dataKey]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoizedConfig = useMemo(() => config, [configKey]);

    const fetchData = useCallback(
        async (cancelToken, overrideOptions = {}, { rethrow = false } = {}) => {
            const finalUrl = overrideOptions.url || url;
            const finalMethod = overrideOptions.method || method;
            const finalParams = overrideOptions.params || memoizedParams;
            const finalData = overrideOptions.data || memoizedData;
            const finalConfig = overrideOptions.config
                ? { ...memoizedConfig, ...overrideOptions.config }
                : memoizedConfig;

            const finalOnSuccess = overrideOptions.onSuccess || onSuccessRef.current;
            const finalOnError = overrideOptions.onError || onErrorRef.current;

            if (!finalUrl) return;

            setLoading(true);
            setError(null);

            try {
                let response;

                if (finalMethod.toLowerCase() === "get") {
                    response = await apiInstance[finalMethod](finalUrl, finalParams, finalData, {
                        cancelToken,
                        ...finalConfig,
                    });
                } else {
                    response = await apiInstance[finalMethod](finalUrl, finalData, finalParams, {
                        cancelToken,
                        ...finalConfig,
                    });
                }

                if (!Axios.isCancel(response)) {
                    setData(response.data);
                    if (finalOnSuccess) {
                        finalOnSuccess(response.data);
                    }
                    return response.data;
                }
            } catch (err) {
                if (!Axios.isCancel(err)) {
                    setError(err);
                    setData(null);
                    if (finalOnError) {
                        finalOnError(err);
                    }
                    if (rethrow) throw err;
                }
            } finally {
                setLoading(false);
            }
        },
        [url, method, memoizedParams, memoizedData, memoizedConfig, apiInstance]
    );

    useEffect(() => {
        if (!enabled || manual) {
            setLoading(false);
            return;
        }

        const CancelToken = Axios.CancelToken;
        const source = CancelToken.source();
        fetchData(source.token);

        return () => {
            source.cancel("Request canceled");
        };
    }, [fetchData, enabled, manual]);

    const refetch = useCallback(() => {
        if (!enabled || !url) return Promise.resolve();
        const CancelToken = Axios.CancelToken;
        const source = CancelToken.source();
        return fetchData(source.token);
    }, [fetchData, enabled, url]);

    const mutate = useCallback(
        async (mutateOptions = {}) => {
            const CancelToken = Axios.CancelToken;
            const source = CancelToken.source();
            try {
                return await fetchData(source.token, mutateOptions, { rethrow: true });
            } catch (error) {
                throw error;
            }
        },
        [fetchData]
    );

    return { apiData, loading, error, refetch, mutate };
}

export default useFetch;
